import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCheckDouble, FaTruck, FaWeightHanging, FaMapMarkerAlt, 
  FaCalendarAlt, FaFileInvoice, FaEye, FaSearch 
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { jobs } from '../api';

function CompletedJobs({ onLogout }) {
  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchCompletedJobs();
  }, []);

  const fetchCompletedJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const jobsData = await jobs.getAll();
      setJobsList(jobsData.jobs?.filter(j => j.status === 'completed') || []);
    } catch (err) {
      console.error('Error fetching completed jobs:', err);
      setError('Failed to load completed jobs.');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobsList.filter(job =>
    job.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.client?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.commodity?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout activeTab="completed" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading completed jobs...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeTab="completed" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Jobs</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={fetchCompletedJobs}
            className="mt-4 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="completed" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">✅ Completed Jobs</h1>
          <p className="text-gray-500 text-sm">All completed and closed jobs</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search completed jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green w-64"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <FaCheckDouble size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{jobsList.length}</div>
              <div className="text-xs text-gray-500">Completed Jobs</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <FaTruck size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">
                {jobsList.reduce((sum, job) => sum + (job.total_quantity_mt || 0), 0)} MT
              </div>
              <div className="text-xs text-gray-500">Total Tonnage</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
              <FaFileInvoice size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">
                R{jobsList.reduce((sum, job) => sum + (job.estimated_value || 0), 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total Revenue</div>
            </div>
          </div>
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-navy mb-2">No Completed Jobs</h3>
          <p className="text-gray-500">Jobs will appear here once they are marked as completed.</p>
        </div>
      ) : (
        filteredJobs.map((job) => (
          <div key={job.id} className="bg-white rounded-xl border border-gray-200 p-6 mb-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold text-navy">{job.job_number}</h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600">
                    ✅ COMPLETED
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <span className="font-medium">{job.client?.company_name || 'Unknown Client'}</span>
                  <span className="text-gray-400 mx-2">•</span>
                  <span>{job.commodity || 'Various'}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <FaWeightHanging size={14} />
                    {job.total_quantity_mt || 0} MT
                  </span>
                  <span className="flex items-center gap-1">
                    <FaMapMarkerAlt size={14} />
                    {job.origin} → {job.destination}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaCalendarAlt size={14} />
                    {job.start_date} → {job.actual_end_date || job.estimated_end_date}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaFileInvoice size={14} />
                    R{job.estimated_value?.toLocaleString() || 'N/A'}
                  </span>
                </div>

                <div className="mb-3">
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span>Progress</span>
                    <span className="font-medium text-green-600">100% Complete</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Link 
                    to={`/completed-jobs/${job.id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors"
                  >
                    <FaEye size={14} /> View
                  </Link>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-green hover:text-green transition-colors">
                    <FaFileInvoice size={14} /> Invoice
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-yellow-500 hover:text-yellow-500 transition-colors">
                    Reopen
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-red-500 hover:text-red-500 transition-colors">
                    Archive
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </AdminLayout>
  );
}

export default CompletedJobs;
